<script setup lang="ts">
import type { CSSProperties } from 'vue'
import { isPlainText, escapeHtml } from '~/utils/bio'

const props = defineProps<{
  bio: string | null
  bioStyle?: CSSProperties
}>()

// Lazy-load DOMPurify on client only
let purify: typeof import('dompurify').default | null = null

if (import.meta.client) {
  import('dompurify').then((mod) => {
    purify = mod.default
  })
}

// Allowlist matching server/utils/sanitizeBio.ts
const ALLOWED_TAGS = [
  'b', 'i', 'em', 'strong', 's', 'code',
  'p', 'br', 'hr',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'blockquote', 'pre'
]
const ALLOWED_ATTR: string[] = []

function sanitize(html: string): string {
  if (purify) {
    return purify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR
    })
  }
  // SSR fallback: allow only safe tags (same allowlist as server util)
  return html
}

const sanitizedBio = computed(() => {
  if (!props.bio) return ''

  // Normalize plain text bios to HTML
  const html = isPlainText(props.bio)
    ? props.bio
        .split('\n')
        .filter(l => l.trim())
        .map(l => `<p>${escapeHtml(l)}</p>`)
        .join('')
    : props.bio

  return sanitize(html)
})
</script>

<template>
  <div
    v-if="sanitizedBio"
    class="bio-display leading-relaxed"
    :style="bioStyle"
    v-html="sanitizedBio"
  />
</template>

<style>
.bio-display p {
  margin: 0;
}

.bio-display p + p {
  margin-top: 0.25em;
}

.bio-display ul,
.bio-display ol {
  padding-left: 1.25em;
  margin: 0.25em 0;
}

.bio-display li {
  margin: 0.1em 0;
}

.bio-display h1,
.bio-display h2,
.bio-display h3,
.bio-display h4,
.bio-display h5,
.bio-display h6 {
  font-weight: 700;
  line-height: 1.2;
}

.bio-display h1 { font-size: 1.75em; }
.bio-display h2 { font-size: 1.5em; }
.bio-display h3 { font-size: 1.25em; }
.bio-display h4 { font-size: 1.1em; }
.bio-display h5 { font-size: 1em; }
.bio-display h6 { font-size: 0.9em; }

.bio-display blockquote {
  border-left: 3px solid rgba(255, 255, 255, 0.15);
  padding-left: 0.75em;
  margin: 0.5em 0;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
}

.bio-display code {
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  padding: 0.15em 0.3em;
  font-size: 0.9em;
}

.bio-display pre {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 0.75em 1em;
  margin: 0.5em 0;
  overflow-x: auto;
}

.bio-display pre code {
  background: none;
  padding: 0;
  border-radius: 0;
  font-size: 0.85em;
}

.bio-display hr {
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 0.75em 0;
}
</style>
